import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiBaseUrl } from '../api/client'
import type {
    ContactService,
    RemediationScenario,
    ScenarioDefinition,
    ScenarioLintError,
    ScenarioPreview,
    ScenarioRun,
    ScenarioService,
    ScenarioVocabulary,
} from '../api/schema'

// Remediation scenarios (spec 2026-10-28-15): the catalogue, the runs on a
// ticket and the services a contact holds.

export type { ContactService, RemediationScenario, ScenarioDefinition, ScenarioLintError, ScenarioPreview, ScenarioRun, ScenarioService, ScenarioVocabulary }

export type I18nLabel = Record<string, string> | undefined

/** The label in the reader's language, else English, else the first one. */
export function labelIn(label: I18nLabel, lang: string, fallback = ''): string {
    if (!label) return fallback
    const short = lang.slice(0, 2)
    return label[short] || label.en || Object.values(label)[0] || fallback
}

export type ApiError = { status: number; code?: string; title?: string; errors?: ScenarioLintError[] }

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const token = localStorage.getItem('token')
    const res = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options?.headers },
    })
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw { status: res.status, ...body } as ApiError
    }
    if (res.status === 204) return undefined as T
    return res.json()
}

const base = (org: string) => `/api/v1/organizations/${org}`

export const scenarioKeys = {
    list: (org: string) => ['scenarios', org] as const,
    vocabulary: (org: string) => ['scenarios', org, 'vocabulary'] as const,
    candidates: (org: string, issue: string) => ['scenarios', org, 'candidates', issue] as const,
    run: (org: string, issue: string) => ['scenarios', org, 'run', issue] as const,
    services: (org: string, user: string) => ['scenarios', org, 'services', user] as const,
}

export function useScenarios(org: string, filters: { universe?: string; origin?: string; q?: string } = {}) {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v))
    return useQuery({
        queryKey: [...scenarioKeys.list(org), filters],
        queryFn: () => apiFetch<{ data: RemediationScenario[] }>(`${base(org)}/compliance/scenarios?${params}`).then((r) => r.data),
        enabled: !!org,
    })
}

export function useScenarioVocabulary(org: string) {
    return useQuery({
        queryKey: scenarioKeys.vocabulary(org),
        queryFn: () => apiFetch<ScenarioVocabulary>(`${base(org)}/compliance/scenarios/vocabulary`),
        enabled: !!org,
        staleTime: 5 * 60 * 1000,
    })
}

function useInvalidate(org: string) {
    const qc = useQueryClient()
    return () => {
        qc.invalidateQueries({ queryKey: ['scenarios', org] })
    }
}

export function useUpdateScenario(org: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: ({ uid, body }: { uid: string; body: Record<string, unknown> }) =>
            apiFetch<RemediationScenario>(`${base(org)}/compliance/scenarios/${uid}`, { method: 'PATCH', body: JSON.stringify(body) }),
        onSuccess: invalidate,
    })
}

export function useCreateScenario(org: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: (body: Record<string, unknown>) =>
            apiFetch<RemediationScenario>(`${base(org)}/compliance/scenarios`, { method: 'POST', body: JSON.stringify(body) }),
        onSuccess: invalidate,
    })
}

export function useCloneScenario(org: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: ({ uid, replace = true }: { uid: string; replace?: boolean }) =>
            apiFetch<RemediationScenario>(`${base(org)}/compliance/scenarios/${uid}/clone`, { method: 'POST', body: JSON.stringify({ replace }) }),
        onSuccess: invalidate,
    })
}

export function useProposeScenario(org: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: (uid: string) => apiFetch<RemediationScenario>(`${base(org)}/compliance/scenarios/${uid}/propose`, { method: 'POST' }),
        onSuccess: invalidate,
    })
}

export function useStartBacklog(org: string) {
    const invalidate = useInvalidate(org)
    return useMutation({
        mutationFn: (uid: string) => apiFetch<{ started: number }>(`${base(org)}/compliance/scenarios/${uid}/start-backlog`, { method: 'POST' }),
        onSuccess: invalidate,
    })
}

export function useValidateScenario(org: string) {
    return useMutation({
        mutationFn: (body: Record<string, unknown>) =>
            apiFetch<{ valid: boolean; errors: ScenarioLintError[] }>(`${base(org)}/compliance/scenarios/validate`, { method: 'POST', body: JSON.stringify(body) }),
    })
}

export function useDraftPreview(org: string) {
    return useMutation({
        mutationFn: (body: { issue_uid: string; scenario: Record<string, unknown> }) =>
            apiFetch<ScenarioPreview>(`${base(org)}/compliance/scenarios/preview`, { method: 'POST', body: JSON.stringify(body) }),
    })
}

export type CandidateScenario = { scenario: RemediationScenario; rank: number; matched: boolean }

export function useIssueScenarios(org: string, issueUid: string, enabled = true) {
    return useQuery({
        queryKey: scenarioKeys.candidates(org, issueUid),
        queryFn: () => apiFetch<{ data: CandidateScenario[] }>(`${base(org)}/issues/${issueUid}/scenarios`).then((r) => r.data),
        enabled: enabled && !!org && !!issueUid,
    })
}

export function useScenarioPreview(org: string, issueUid: string) {
    return useMutation({
        mutationFn: (scenarioCode: string) =>
            apiFetch<ScenarioPreview>(`${base(org)}/issues/${issueUid}/scenario-preview`, { method: 'POST', body: JSON.stringify({ scenario_code: scenarioCode }) }),
    })
}

/** The run of a dossier; null when no scenario ever ran on it. */
export function useScenarioRun(org: string, issueUid: string, enabled = true) {
    return useQuery({
        queryKey: scenarioKeys.run(org, issueUid),
        queryFn: () =>
            apiFetch<ScenarioRun>(`${base(org)}/issues/${issueUid}/scenario`)
                // A body without a `run` is no run: the card dereferences it.
                .then((r) => (r?.run ? r : null))
                .catch((e: ApiError) => {
                    if (e.status === 404) return null
                    throw e
                }),
        enabled: enabled && !!org && !!issueUid,
    })
}

function useInvalidateIssue(org: string) {
    const qc = useQueryClient()
    return () => {
        qc.invalidateQueries({ queryKey: ['scenarios', org] })
        qc.invalidateQueries()
    }
}

export function useStartScenario(org: string, issueUid: string) {
    const invalidate = useInvalidateIssue(org)
    return useMutation({
        mutationFn: (scenarioCode: string) =>
            apiFetch<ScenarioRun>(`${base(org)}/issues/${issueUid}/scenario`, { method: 'POST', body: JSON.stringify({ scenario_code: scenarioCode }) }),
        onSuccess: invalidate,
    })
}

export function useCancelScenario(org: string, issueUid: string) {
    const invalidate = useInvalidateIssue(org)
    return useMutation({
        mutationFn: (reason: string) =>
            apiFetch<void>(`${base(org)}/issues/${issueUid}/scenario`, { method: 'DELETE', body: JSON.stringify({ reason }) }),
        onSuccess: invalidate,
    })
}

export function useDecide(org: string, issueUid: string) {
    const invalidate = useInvalidateIssue(org)
    return useMutation({
        mutationFn: (body: { step?: string; option: string; contact_uid?: string; document_uid?: string; note?: string }) =>
            apiFetch<ScenarioRun>(`${base(org)}/issues/${issueUid}/decision`, { method: 'POST', body: JSON.stringify(body) }),
        onSuccess: invalidate,
    })
}

export function useContactServices(org: string, userUid: string) {
    return useQuery({
        queryKey: scenarioKeys.services(org, userUid),
        queryFn: () => apiFetch<{ data: ContactService[]; vocabulary: ScenarioService[] }>(`${base(org)}/users/${userUid}/services`),
        enabled: !!org && !!userUid,
    })
}

export function useAddContactService(org: string, userUid: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (body: { service_code: string; scope_asset_uid?: string }) =>
            apiFetch<ContactService>(`${base(org)}/users/${userUid}/services`, { method: 'POST', body: JSON.stringify(body) }),
        onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.services(org, userUid) }),
    })
}

export function useRemoveContactService(org: string, userUid: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (uid: string) => apiFetch<void>(`${base(org)}/users/${userUid}/services/${uid}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: scenarioKeys.services(org, userUid) }),
    })
}

/** Records, from a compose dialog, that a contact holds a service. */
export async function rememberServiceHolder(org: string, userUid: string, serviceCode: string) {
    return apiFetch<ContactService>(`${base(org)}/users/${userUid}/services`, { method: 'POST', body: JSON.stringify({ service_code: serviceCode }) })
}

/** Searches the organization's contacts that have an e-mail. */
export async function searchContacts(org: string, q: string): Promise<{ uid: string; name?: string; email?: string }[]> {
    const res = await apiFetch<{ data?: { uid: string; name?: string; email?: string }[] }>(
        `${base(org)}/users?q=${encodeURIComponent(q)}&limit=10`,
    )
    return (res.data ?? []).filter((u) => !!u.email)
}

/** A duration in seconds as a short text (« 4 h », « 3 j »). */
export function formatSeconds(seconds: number | null | undefined, t: (k: string, o?: Record<string, unknown>) => string): string {
    if (seconds == null) return '—'
    if (seconds < 3600) return t('scenarios:duration.minutes', { count: Math.max(1, Math.round(seconds / 60)) })
    if (seconds < 48 * 3600) return t('scenarios:duration.hours', { count: Math.round(seconds / 3600) })
    return t('scenarios:duration.days', { count: Math.round(seconds / 86400) })
}
